/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CodecField.tsx
 🗓️ Created: 2025-01-11
 🧩 Description:
    Compact codec selector field with chips and modal popup.

 🔧 Features:
    - Display selected codecs as chips
    - Remove individual codecs with X button
    - Open modal to select/reorder codecs
    - Compact single-line display

 🔁 Last Updated: 2025-01-11 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState } from 'react';
import { X, Settings } from 'lucide-react';
import { CodecSelectorModal } from './CodecSelectorModal';

interface CodecFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  videoOnly?: boolean;
  allowVideo?: boolean;
  placeholder?: string;
}

export function CodecField({
  id,
  value,
  onChange,
  disabled = false,
  videoOnly = false,
  allowVideo = true,
  placeholder = 'Select codecs...'
}: CodecFieldProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedCodecs = value
    ? value.split(',').map(c => c.trim()).filter(Boolean)
    : [];

  const handleRemoveCodec = (codecToRemove: string) => {
    if (disabled) return;
    const newCodecs = selectedCodecs.filter(c => c !== codecToRemove);
    onChange(newCodecs.join(','));
  };

  const handleOpenModal = () => {
    if (!disabled) {
      setIsModalOpen(true);
    }
  };

  // Show all codecs in the field (flexible width)
  const displayCodecs = selectedCodecs;

  return (
    <>
      <div
        className={`
          flex items-center gap-1.5 px-3 h-10 w-full
          bg-background border border-input rounded-md
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'}
          transition-colors
        `}
        onClick={handleOpenModal}
      >
        {/* Hidden input for label association */}
        {id && (
          <input
            id={id}
            type="text"
            className="sr-only"
            readOnly
            value={value}
            onFocus={handleOpenModal}
            tabIndex={-1}
            aria-hidden="true"
          />
        )}
        {/* Selected Codec Chips */}
        {selectedCodecs.length > 0 ? (
          <div className="flex items-center gap-1.5 flex-1 overflow-x-auto scrollbar-none">
            {displayCodecs.map((codec) => (
              <span
                key={codec}
                className="
                  inline-flex items-center gap-1 px-2 py-0.5
                  bg-primary/10 text-primary text-xs font-medium rounded
                  border border-primary/20 flex-shrink-0
                "
              >
                {codec}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCodec(codec);
                    }}
                    className="hover:bg-primary/20 rounded p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="flex-1 text-sm text-muted-foreground">
            {placeholder}
          </span>
        )}

        {/* Settings Icon */}
        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal();
            }}
            className="p-1.5 hover:bg-muted rounded transition-colors flex-shrink-0"
          >
            <Settings className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Modal */}
      <CodecSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        value={value}
        onChange={onChange}
        videoOnly={videoOnly}
        allowVideo={allowVideo}
      />
    </>
  );
}

